/**
 * WHY AN ASSIGNMENT DID NOT HAPPEN — named, never "failed".
 *
 * This module exists because of a live bug on 2026-09-28: every rep assignment
 * from the Command Center had been returning 400 `reason_required` — the client
 * never sent the `reason` the ledger demands — and what reached the person was
 * a sentence telling them to include a field the screen has never offered.
 * Brian read it, reasonably, as "it says failed".
 *
 * THE RULE THIS FILE ENFORCES: a permission refusal, a transport error and a
 * validation rejection must never read the same, and none of them may render as
 * a bare failure. "Failed" is a claim with no content — it tells the person
 * nothing about which of three completely different problems they have, and the
 * three have opposite fixes: wait and retry, ask for a grant, or change the
 * input. Same defect class as the artwork upload and the team-room thread.
 *
 * So every failure resolves to four things:
 *   kind    which of the causes it is, for styling and for telemetry
 *   title   the short claim, in the person's words, not the enum's
 *   detail  what the server actually said, when it said something useful
 *   action  what THIS person can do next — the part "failed" never has
 *
 * `explainAssignFailure()` is total: it always returns all four, for any input,
 * including null, a bare string, or an error shape nobody anticipated. A
 * surface that renders its output cannot render an empty message, which is the
 * property that makes the rule hold rather than merely be stated.
 */

/** The causes. Kept as an exported map so surfaces can style/branch on them. */
export const FAILURE_KIND = {
  PERMISSION: 'permission',
  VALIDATION: 'validation',
  TRANSPORT: 'transport',
  AUTH: 'auth',
  CONFLICT: 'conflict',
  SERVER: 'server',
  UNKNOWN: 'unknown',
}

/**
 * Server error tokens that are PERMISSION refusals, whatever status carries
 * them. Named explicitly rather than inferred from the 403, because the same
 * status also carries `field_forbidden`, which is a different conversation.
 */
const PERMISSION_TOKENS = new Set([
  'assignment_locked',
  'data_repair_locked',
  'automation_forbidden',
  'field_forbidden',
  'forbidden',
  'permission_check_failed',
  // Cheer lane delegation: the record or the destination is outside the lane.
  'lane_record_outside',
  'lane_destination_outside',
  'lane_record_not_cheer',
])

/** Server error tokens that mean "the request was wrong", not "you may not". */
const VALIDATION_TOKENS = new Set([
  'reason_required',
  'id required',
  'id_required',
  'service_required',
  'invalid_field',
  'nothing_to_write',
  // A lane delegate may move a seat between lane members, never empty it.
  'lane_cannot_clear',
])

const str = (v) => (typeof v === 'string' ? v.trim() : '')

/** The prose the backend wrote, if it wrote any. Never an enum token. */
function serverProse(body) {
  const reason = str(body?.reason)
  const detail = typeof body?.detail === 'string' ? str(body.detail) : ''
  const message = str(body?.message)
  const prose = reason || detail || message
  // A value with no whitespace is a machine token (`reason_required`), not a
  // sentence. Showing one to a person is the bug this module exists to stop.
  if (!prose || !/\s/.test(prose)) return ''
  return prose
}

/**
 * Classify one failed assignment.
 *
 * @param err   the thrown ApiError (or anything else that reached the catch)
 * @param ctx   { recordName, rep, count } — used only to word the message
 * @returns {{ kind, title, detail, action, message, status, token }}
 */
export function explainAssignFailure(err, ctx = {}) {
  const status = Number.isFinite(err?.status) ? err.status : null
  const body = err?.body || null
  const token = str(body?.error)
  const prose = serverProse(body)
  const who = ctx.rep ? `to ${ctx.rep}` : ''
  const what = ctx.count > 1
    ? `${ctx.count.toLocaleString()} records`
    : (ctx.recordName ? `“${ctx.recordName}”` : 'this record')

  const done = (kind, title, action, detail = prose) => ({
    kind,
    title,
    detail,
    action,
    status,
    token: token || null,
    // One ready-made sentence for surfaces that render a single string. Built
    // from the parts so it can never come out empty.
    message: [title, detail, action].filter(Boolean).join(' '),
  })

  /* ---- transport: the request never got an answer --------------------- */
  // status 0 is lib/api.js's own network-failure marker; a 502/503/504 is the
  // proxy or the backend being unreachable rather than the request being wrong.
  if (status === 0 || status === 502 || status === 503 || status === 504) {
    return done(
      FAILURE_KIND.TRANSPORT,
      `Couldn’t reach the server, so nothing was assigned ${who}.`.replace(/\s+/g, ' '),
      'Nothing was changed. Check your connection and try again. If it keeps happening, the API may be down.',
      status === 0 ? '' : `The server answered ${status}.`,
    )
  }

  // A 404 on this route means the endpoint is not deployed, which is an
  // infrastructure problem and not something the person can fix by retrying.
  if (status === 404 || err?.missingRoute) {
    return done(
      FAILURE_KIND.TRANSPORT,
      'The assignment endpoint isn’t available on the backend.',
      'Nothing was changed. This needs a deploy, so send it to Brian rather than retrying.',
    )
  }

  /* ---- auth: the session, not the permission -------------------------- */
  if (status === 401) {
    return done(
      FAILURE_KIND.AUTH,
      'Your session has expired.',
      'Nothing was changed. Sign in again and redo the assignment.',
    )
  }

  /* ---- permission: you are not allowed to do this --------------------- */
  if (PERMISSION_TOKENS.has(token) || (status === 403 && !VALIDATION_TOKENS.has(token))) {
    return done(
      FAILURE_KIND.PERMISSION,
      `You don’t have permission to change the assigned rep on ${what}.`,
      'Nothing was changed. Ask an executive to grant you “Manage Assignments”. The attempt has been recorded.',
    )
  }

  /* ---- conflict ------------------------------------------------------- */
  if (status === 409) {
    return done(
      FAILURE_KIND.CONFLICT,
      `${what} changed while you were working on it.`,
      'Nothing was changed. Refresh the record and try again.',
    )
  }

  /* ---- validation: the request was wrong ------------------------------ */
  if (status === 400 || VALIDATION_TOKENS.has(token)) {
    // `reason_required` is the one the person can actually act on, so it gets
    // the words that name the control rather than the field.
    if (token === 'reason_required') {
      return done(
        FAILURE_KIND.VALIDATION,
        `Nothing was assigned ${who}, because an assignment needs a reason.`.replace(/\s+/g, ' '),
        'Type a short reason for the change and assign again. It is written to the permanent audit ledger.',
        '',
      )
    }
    return done(
      FAILURE_KIND.VALIDATION,
      `The assignment was rejected as invalid, so ${what} was not changed.`,
      'Correct the highlighted details and try again.',
    )
  }

  /* ---- the server broke ----------------------------------------------- */
  if (status && status >= 500) {
    return done(
      FAILURE_KIND.SERVER,
      `The server hit an error and ${what} was not changed.`,
      'Nothing was changed. Try once more, and if it repeats, send this to Brian.',
    )
  }

  /* ---- the backstop ---------------------------------------------------
   * Even here it names a status or repeats what the server said. This branch
   * is the one that used to render "failed"; it is not allowed to any more. */
  const fallbackDetail = prose || str(err?.message)
  return done(
    FAILURE_KIND.UNKNOWN,
    `${what} was not assigned${who ? ' ' + who : ''}.`,
    'Nothing was changed. Try again, and if it repeats, send this to Brian with what you clicked.',
    fallbackDetail || (status ? `The server answered ${status} with no explanation.` : ''),
  )
}

/**
 * The same, for a bulk run where failures may have different causes.
 * Reports the count AND the dominant reason — "142 of 300 could not be written"
 * is the bulk version of "failed" and tells nobody anything.
 */
export function explainBulkFailures(errors, { total, done: okCount, rep } = {}) {
  const list = (errors || []).filter(Boolean)
  if (!list.length) return null

  const explained = list.map((e) => explainAssignFailure(e, { rep }))
  const tally = new Map()
  for (const x of explained) tally.set(x.kind, (tally.get(x.kind) || 0) + 1)
  const [dominantKind] = [...tally.entries()].sort((a, b) => b[1] - a[1])[0]
  const lead = explained.find((x) => x.kind === dominantKind)
  const mixed = tally.size > 1

  const failedCount = list.length
  return {
    kind: dominantKind,
    count: failedCount,
    message:
      `${failedCount.toLocaleString()} of ${Number(total || failedCount).toLocaleString()} could not be assigned` +
      `${okCount ? ` (${okCount.toLocaleString()} succeeded)` : ''}. ` +
      `${mixed ? 'Most of them: ' : ''}${lead.title} ${lead.action}`.replace(/\s+/g, ' '),
  }
}
